import FingerprintJS, { type Agent, type GetResult } from '@fingerprintjs/fingerprintjs';
import { UAParser } from 'ua-parser-js';
import { setContext, setTag, setUser, withScope } from '@sentry/react';
import { $profileStore } from '@lib/store';
import { DateUtils } from './dateUtils';

const Channel = {
  CorporateOsInsignia: 'Corporate OS Insignia',
  CorporateOsVcfc: 'Corporate OS VCFC',
  PersonalOS: 'Personal OS',
} as const;
type Channel = (typeof Channel)[keyof typeof Channel];

const DeviceType = {
  Web: 'Web',
  Android: 'Android',
  IOS: 'IOS',
} as const;
type DeviceType = (typeof DeviceType)[keyof typeof DeviceType];

export interface TrackingInfo {
  timestamp: string;
  username: string | undefined;
  userId: string | undefined;
  channel: Channel;
  deviceType: DeviceType;
  deviceFingerprint: GetResult;
  os: {
    name: string | undefined;
    version: string | undefined;
  };
  browser: {
    name: string | undefined;
    version: string | undefined;
  };
  appVersion: string | undefined;
  userAgent: string;
  language: string;
  languages: readonly string[];
  timezone: string;
  screen: {
    width: number;
    height: number;
    colorDepth: number;
    pixelRatio: number;
  };
  viewport: {
    width: number;
    height: number;
  };
  device: {
    vendor: string | undefined;
    model: string | undefined;
    type: string | undefined;
    memoryGb: number | undefined;
    cpuCores: number | undefined;
    touchPoints: number;
  };
  network: {
    online: boolean;
    effectiveType: string | undefined;
    downlink: number | undefined;
    rtt: number | undefined;
  };
  platform: string;
  cookiesEnabled: boolean;
  doNotTrack: string | null;
}

export class TrackUtils {
  private static fpPromise: Promise<Agent> | null = null;

  private static getFingerprintAgent = () => {
    if (!this.fpPromise) this.fpPromise = FingerprintJS.load();
    return this.fpPromise;
  };

  private static getDeviceFingerprint = async () => {
    const fp = await this.getFingerprintAgent();
    return fp.get();
  };

  private static parseUserAgent = async () => {
    const parser = new UAParser();
    const result = parser.getResult();
    return (await result.withClientHints()) as typeof result;
  };

  private static getChannel = () => {
    if (process.env.COLOR_PALETTE?.toLowerCase() === 'insignia') return Channel.CorporateOsInsignia;
    if (process.env.COLOR_PALETTE?.toLowerCase() === 'vcfc') return Channel.CorporateOsVcfc;
    if (process.env.COLOR_PALETTE?.toLowerCase() === 'personal') return Channel.PersonalOS;
    return Channel.CorporateOsInsignia;
  };

  static collectTrackingInfo = async (): Promise<TrackingInfo> => {
    const [deviceFingerprint, ua] = await Promise.all([this.getDeviceFingerprint(), this.parseUserAgent()]);
    const profile = $profileStore.store.getState().data;

    return {
      timestamp: DateUtils.getCurrentDateUTC(),
      username: profile?.userName,
      userId: profile?.id,
      channel: this.getChannel(),
      deviceType: DeviceType.Web,
      deviceFingerprint,
      os: { name: ua.os.name, version: ua.os.version },
      browser: { name: ua.browser.name, version: ua.browser.version },
      appVersion: process.env.BUILD_DATE,
      userAgent: ua.ua || navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth,
        pixelRatio: window.devicePixelRatio,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      device: {
        vendor: ua.device.vendor,
        model: ua.device.model,
        type: ua.device.type,
        memoryGb: navigator.deviceMemory,
        cpuCores: navigator.hardwareConcurrency,
        touchPoints: navigator.maxTouchPoints,
      },
      network: {
        online: navigator.onLine,
        downlink: navigator.connection?.downlink,
        effectiveType: navigator.connection?.effectiveType,
        rtt: navigator.connection?.rtt,
      },
      platform: navigator.platform,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
    };
  };

  static sendTrackingInfoToSentry = async () => {
    const info = await this.collectTrackingInfo();
    const { deviceFingerprint, ...tracking } = info;

    if (info.userId || info.username) setUser({ id: info.userId, username: info.username });

    setTag('channel', info.channel);
    setTag('device_fingerprint', deviceFingerprint.visitorId);
    if (info.username) setTag('username', info.username);
    if (info.userId) setTag('user_id', info.userId);

    setContext('tracking', { ...tracking, deviceFingerprintId: deviceFingerprint.visitorId });

    withScope((scope) => {
      scope.addAttachment({ filename: 'device-fingerprint.json', data: JSON.stringify(deviceFingerprint, null, 2), contentType: 'application/json' });
      scope.captureEvent({ level: 'info', message: 'Device fingerprint collected on login', transaction: deviceFingerprint.visitorId });
    });

    return info;
  };
}
